// src/pages/AuthPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Calendar, Mail, Lock, User, Building2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import posthog from 'posthog-js';
import { buildAttribution, attributionEventProps, PENDING_ATTR_KEY } from '../lib/attribution';
import styles from './AuthPage.module.css';

function GoogleLogo() {
  return (
    <svg className={styles.googleLogo} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

// Same-origin path guard. Anything that isn't a single relative path is
// rejected — no absolute URLs, no protocol-relative `//host`, no scheme.
// This is the open-redirect defence for the `redirect` query parameter.
function sanitiseRedirect(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  if (!/^\/[a-zA-Z0-9/_\-?=&%.]*$/.test(raw)) return null;
  return raw;
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const safeRedirect = sanitiseRedirect(searchParams.get('redirect'));
  const returnUrl = safeRedirect || location.state?.returnUrl || '/dashboard';
  const prefillEmail = searchParams.get('prefill_email') || '';
  const hint = searchParams.get('hint');

  const [mode, setMode] = useState(hint === 'signup' ? 'signup' : 'login');

  useEffect(() => { if (user) navigate(returnUrl); }, [user, navigate, returnUrl]);

  return (
    <div className={styles.page}>
      <div className={styles.cardWrap}>
        {mode === 'login' ? (
          <LoginForm
            onSwitchToSignup={() => setMode('signup')}
            prefillEmail={prefillEmail}
            returnUrl={returnUrl}
          />
        ) : (
          <SignupForm
            onSwitchToLogin={() => setMode('login')}
            prefillEmail={prefillEmail}
            returnUrl={returnUrl}
          />
        )}
      </div>
    </div>
  );
}

function LoginForm({ onSwitchToSignup, prefillEmail = '', returnUrl = '/dashboard' }) {
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // When the email is prefilled (e.g. from an invitation flow), lock it.
  // The invitee must sign in with the address the invitation was sent to;
  // letting them edit it would only produce 403s downstream.
  const emailLocked = !!prefillEmail;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const { error } = await signIn(email, password);
    if (error) setError(error.message);
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError(''); setLoading(true);
    const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(returnUrl)}`;
    const { error } = await signInWithGoogle({ redirectTo });
    if (error) { setError(error.message); setLoading(false); }
  };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.headerLogoWrap}>
          <Calendar className={styles.headerLogo} size={32} aria-hidden="true" />
        </div>
        <h1 className={styles.headerTitle}>Vennio</h1>
        <p className={styles.headerSubtitle}>Infrastructure for Time</p>
      </div>

      {/* Value prop */}
      <div className={styles.valueProp}>
        <h2 className={styles.valuePropTitle}>Scheduling, inverted.</h2>
        <p className={styles.valuePropBody}>
          Instead of sharing your calendar and hoping for the best, see when others are free and propose the right time.
        </p>
        <p className={styles.valuePropNote}>
          Built for outbound teams, AI agents, and anyone tired of calendar link ping-pong.
        </p>
      </div>

      {/* Form */}
      <div className={styles.formSection}>
        {error && (
          <div className={styles.alertError}>
            <AlertCircle size={20} className={styles.alertIcon} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* Email/Password — temporarily re-enabled */}
        {true && (
          <div className={styles.fields}>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <div className={styles.inputWrap}>
                <Mail className={styles.inputIcon} size={20} aria-hidden="true" />
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className={styles.input} placeholder="you@example.com"
                  readOnly={emailLocked}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Password</label>
              <div className={styles.inputWrap}>
                <Lock className={styles.inputIcon} size={20} aria-hidden="true" />
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className={styles.input} placeholder="••••••••"
                />
              </div>
            </div>

            <button onClick={handleSubmit} disabled={loading} className={styles.primaryBtn}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <p className={styles.earlyAccessNote}>Free tier — 1,000 bookings/month, no credit card required</p>
          </div>
        )}

        <div className={true ? styles.googleWrap : ''}>
          {true && (
            <div className={styles.divider}>
              <span className={styles.dividerText}>Or continue with</span>
            </div>
          )}
          <button onClick={handleGoogleSignIn} disabled={loading} className={styles.googleBtn}>
            <GoogleLogo />
            Sign in with Google
          </button>
        </div>

        <p className={styles.switchNote}>
          Don't have an account?{' '}
          <button onClick={onSwitchToSignup} className={styles.switchLink}>Sign up</button>
        </p>

        <div className={styles.legalFooter}>
          <Link to="/privacy" className={styles.legalLink}>Privacy Policy</Link>
          <span className={styles.legalDot}>&middot;</span>
          <Link to="/terms" className={styles.legalLink}>Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}

function SignupForm({ onSwitchToLogin, prefillEmail = '', returnUrl = '/dashboard' }) {
  const { signUp, signInWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('customer');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [roleSelected, setRoleSelected] = useState(false);
  const [success, setSuccess] = useState(false);
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);

  // Lock the email field when prefilled (invitation onboarding flow). The
  // invitee must sign up with the address the invitation was sent to;
  // a different address would 403 downstream.
  const emailLocked = !!prefillEmail;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false); setLoading(true);

    if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
    if (!email) { setError('Email is required'); setLoading(false); return; }

    // Full first-touch attribution (utm_* incl. term/content, referrer, derived
    // channel), carried from marketing/docs via query params with a portal
    // first-touch fallback. Lands in raw_user_meta_data → profiles (trigger).
    const attribution = buildAttribution(searchParams);

    // Pass returnUrl through Supabase's confirmation email so the user
    // lands back on the originating page (e.g. /invite/<token>) after
    // verifying their address, not on a hardcoded /dashboard.
    const emailRedirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(returnUrl)}`;

    const { data, error } = await signUp(
      email, password, role, fullName, attribution,
      { emailRedirectTo }
    );

    if (error) {
      if (error.message?.includes('429') || error.status === 429) {
        setError('Too many signup attempts. Please wait a few minutes and try again.');
      } else if (error.message?.includes('already registered') || error.message?.includes('already exists')) {
        setError('This email is already registered. Try signing in instead.');
      } else if (error.message?.includes('valid email')) {
        setError('Please enter a valid email address.');
      } else {
        setError(error.message || 'Failed to create account. Please try again.');
      }
    } else if (data?.user) {
      if (data.user.identities?.length === 0) {
        setError('This email is already registered. Try signing in instead.');
      } else if (!data.session) {
        posthog.identify(data.user.id);
        posthog.capture('signup', { method: 'email', role, confirmed: false, ...attributionEventProps(attribution) });
        setNeedsEmailConfirmation(true); setSuccess(true);
      } else {
        posthog.identify(data.user.id);
        posthog.capture('signup', { method: 'email', role, confirmed: true, ...attributionEventProps(attribution) });
        setSuccess(true);
      }
    }

    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError(''); setLoading(true);
    localStorage.setItem('pendingRole', role);
    // Supabase OAuth can't carry user metadata before the account exists, so
    // stash attribution across the redirect; AuthCallback applies it on a fresh
    // Google signup.
    try {
      localStorage.setItem(PENDING_ATTR_KEY, JSON.stringify(buildAttribution(searchParams)));
    } catch { /* storage unavailable — skip attribution, not signup */ }
    const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(returnUrl)}`;
    const { error } = await signInWithGoogle({ redirectTo });
    if (error) { setError(error.message); setLoading(false); }
  };

  const handleRoleSelection = (selectedRole) => {
    setRole(selectedRole); setRoleSelected(true);
  };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.headerLogoWrap}>
          <Calendar className={styles.headerLogo} size={32} aria-hidden="true" />
        </div>
        <h1 className={styles.headerTitle}>Join Vennio</h1>
        <p className={styles.headerSubtitle}>Infrastructure for Time</p>
      </div>

      <div className={styles.formSection}>
        {error && (
          <div className={styles.alertError}>
            <AlertCircle size={20} className={styles.alertIcon} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className={styles.alertSuccess}>
            <Mail size={20} className={styles.alertIcon} aria-hidden="true" />
            <div>
              {needsEmailConfirmation ? (
                <>
                  <p className={styles.alertHeading}>Check your email!</p>
                  <p className={styles.alertBody}>
                    We've sent a confirmation link to <strong>{email}</strong>.
                    Click the link in the email to activate your account.
                  </p>
                </>
              ) : (
                <>
                  <p className={styles.alertHeading}>Account created!</p>
                  <p className={styles.alertBody}>Redirecting you to the dashboard...</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Role selection */}
        {!success && !roleSelected ? (
          <div className={styles.roleSection}>
            <h2 className={styles.roleSectionTitle}>I am a...</h2>
            <div className={styles.roleGrid}>
              <button
                onClick={() => handleRoleSelection('customer')}
                className={`${styles.roleCard}${role === 'customer' ? ` ${styles.roleCardActive}` : ''}`}
              >
                <User className={`${styles.roleCardIcon}${role === 'customer' ? ` ${styles.roleCardIconActive}` : ''}`} size={32} aria-hidden="true" />
                <div className={styles.roleCardName}>Customer</div>
                <p className={styles.roleCardDesc}>Find mutual availability instantly</p>
              </button>
              <button
                onClick={() => handleRoleSelection('business')}
                className={`${styles.roleCard}${role === 'business' ? ` ${styles.roleCardActive}` : ''}`}
              >
                <Building2 className={`${styles.roleCardIcon}${role === 'business' ? ` ${styles.roleCardIconActive}` : ''}`} size={32} aria-hidden="true" />
                <div className={styles.roleCardName}>Business</div>
                <p className={styles.roleCardDesc}>AI-native scheduling infrastructure</p>
              </button>
            </div>
          </div>
        ) : !success ? (
          <div>
            {/* Role summary row */}
            <div className={styles.roleSummary}>
              <div className={styles.roleSummaryLeft}>
                {role === 'customer'
                  ? <User className={styles.roleSummaryIcon} size={24} aria-hidden="true" />
                  : <Building2 className={styles.roleSummaryIcon} size={24} aria-hidden="true" />
                }
                <div>
                  <div className={styles.roleSummaryName}>
                    Signing up as a {role === 'customer' ? 'Customer' : 'Business'}
                  </div>
                  <div className={styles.roleSummaryDesc}>
                    {role === 'customer' ? 'Book appointments' : 'Manage appointments'}
                  </div>
                </div>
              </div>
              <button onClick={() => setRoleSelected(false)} className={styles.roleChangeBtn}>Change</button>
            </div>

            {/* Fields */}
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label}>Full Name</label>
                <div className={styles.inputWrap}>
                  <User className={styles.inputIcon} size={20} aria-hidden="true" />
                  <input
                    type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    className={styles.input} placeholder="John Smith"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Email</label>
                <div className={styles.inputWrap}>
                  <Mail className={styles.inputIcon} size={20} aria-hidden="true" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className={styles.input} placeholder="you@example.com"
                    readOnly={emailLocked}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Password</label>
                <div className={styles.inputWrap}>
                  <Lock className={styles.inputIcon} size={20} aria-hidden="true" />
                  <input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className={styles.input} placeholder="••••••••"
                  />
                </div>
                <p className={styles.fieldHint}>Must be at least 6 characters</p>
              </div>

              <button onClick={handleSubmit} disabled={loading} className={styles.primaryBtn}>
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </div>

            <div className={styles.divider}>
              <span className={styles.dividerText}>Or continue with</span>
            </div>

            <button onClick={handleGoogleSignIn} disabled={loading} className={styles.googleBtn}>
              <GoogleLogo />
              Sign up with Google
            </button>
          </div>
        ) : null}

        <p className={styles.switchNote}>
          Already have an account?{' '}
          <button onClick={onSwitchToLogin} className={styles.switchLink}>Sign in</button>
        </p>

        <div className={styles.legalFooter}>
          <Link to="/privacy" className={styles.legalLink}>Privacy Policy</Link>
          <span className={styles.legalDot}>&middot;</span>
          <Link to="/terms" className={styles.legalLink}>Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}
